import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Card from "../components/card";
import "../css/seeAllPage.css";
import NavBar from "./nav";
import Footer from "./footer";

import fallbackImages from "../assets/images";
import {
  fetchAccommodations,
  fetchEvents,
  fetchResturants,
  fetchAttractionsites,
  fetchMalls,
  fetchBuses,
} from "../app/dbSlice";
import {
  defaultSmartFilters,
  filterSmartItems,
  getDistanceFromKimberley,
  getOpenStatus,
  getUniqueCategories,
} from "../utils/smartFilters";

const SeeAllPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const state = location.state || {};
  const category = state.category || "";
  const [filters, setFilters] = useState(defaultSmartFilters);

  // ✅ Redux data (same source as CategoryCards)
  const {
    accommodations = [],
    events = [],
    restaurants = [],
    attractionsites = [],
    malls = [],
    buses = [],
  } = useSelector((state) => state.db);

  const items = useMemo(() => {
    const cardData = {
      Accommodation: accommodations,
      Events: events,
      Restaurants: restaurants,
      Attractions: attractionsites,
      "Shopping Malls": malls,
      Buses: buses,
    };

    return cardData[category] || [];
  }, [accommodations, attractionsites, buses, category, events, malls, restaurants]);
  const uniqueCategories = useMemo(() => getUniqueCategories(items), [items]);
  const filteredItems = useMemo(
    () => filterSmartItems(items, filters),
    [filters, items]
  );

  const updateFilter = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(defaultSmartFilters);
  };

  // 🔥 Fetch data if user refreshes page
  useEffect(() => {
    if (!items.length) {
      dispatch(fetchAccommodations());
      dispatch(fetchEvents());
      dispatch(fetchResturants());
      dispatch(fetchAttractionsites());
      dispatch(fetchMalls());
      dispatch(fetchBuses());
    }
  }, [dispatch, items.length]);

  // Hero content
  const heroContent = {
    Accommodation: {
      title: "Explore All Accommodations",
      subtitle:
        "Find the perfect place to stay in Kimberley, from cozy guesthouses to luxurious hotels.",
    },
    Events: {
      title: "Discover All Events",
      subtitle:
        "Experience the best events happening in Kimberley, from festivals to concerts.",
    },
    Restaurants: {
      title: "Taste All Restaurants",
      subtitle:
        "Savor the flavors of Kimberley with our curated list of top restaurants.",
    },
    Attractions: {
      title: "Visit All Attractions",
      subtitle:
        "Explore the must-see attractions in Kimberley, from historical sites to natural wonders.",
    },
    "Shopping Malls": {
      title: "Shop at All Malls",
      subtitle:
        "Discover the best shopping destinations in Kimberley for all your needs.",
    },
    Buses: {
      title: "Find Bus Routes",
      subtitle:
        "Compare travel options and transport services for moving in and out of Kimberley.",
    },
  };

  // 🔥 fallback image matcher (same logic as CategoryCards)
  const getFallbackImages = (itemName) => {
    if (!itemName) return [];

    const matchKey = Object.keys(fallbackImages).find(
      (key) =>
        key.toLowerCase().includes(itemName.toLowerCase()) ||
        itemName.toLowerCase().includes(key.toLowerCase())
    );

    return matchKey ? fallbackImages[matchKey] : [];
  };

  return (
    <div>
      <NavBar />

      {/* Hero Section */}
      <div className="hero-section">
        <h1 className="hero-title">
          {heroContent[category]?.title || "Explore All"}
        </h1>
        <p className="hero-subtitle">
          {heroContent[category]?.subtitle ||
            "Discover the best of Kimberley."}
        </p>
      </div>

      {/* Cards */}
      <div className="see-all-page">
        <section className="smart-filter-panel">
          <div className="smart-filter-heading">
            <div>
              <span>Smart Filters</span>
              <h2>Find the right match faster</h2>
            </div>
            <p>
              Showing {filteredItems.length} of {items.length} results
            </p>
          </div>

          <div className="smart-filter-grid">
            <label>
              Search
              <input
                type="search"
                value={filters.search}
                placeholder="Search name, area, keyword..."
                onChange={(event) => updateFilter("search", event.target.value)}
              />
            </label>

            <label>
              Category
              <select
                value={filters.category}
                onChange={(event) => updateFilter("category", event.target.value)}
              >
                {uniqueCategories.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Max price
              <select
                value={filters.maxPrice}
                onChange={(event) => updateFilter("maxPrice", event.target.value)}
              >
                <option value="any">Any price</option>
                <option value="150">Up to R150</option>
                <option value="500">Up to R500</option>
                <option value="1000">Up to R1 000</option>
                <option value="2000">Up to R2 000</option>
              </select>
            </label>

            <label>
              Rating
              <select
                value={filters.minRating}
                onChange={(event) => updateFilter("minRating", event.target.value)}
              >
                <option value="0">Any rating</option>
                <option value="3">3+ stars</option>
                <option value="4">4+ stars</option>
                <option value="5">5 stars</option>
              </select>
            </label>

            <label>
              Distance
              <select
                value={filters.maxDistance}
                onChange={(event) => updateFilter("maxDistance", event.target.value)}
              >
                <option value="any">Any distance</option>
                <option value="2">Within 2 km</option>
                <option value="5">Within 5 km</option>
                <option value="15">Within 15 km</option>
                <option value="50">Within 50 km</option>
              </select>
            </label>

            <label>
              Sort by
              <select
                value={filters.sortBy}
                onChange={(event) => updateFilter("sortBy", event.target.value)}
              >
                <option value="recommended">Recommended</option>
                <option value="rating">Highest rated</option>
                <option value="price">Lowest price</option>
                <option value="distance">Nearest first</option>
              </select>
            </label>
          </div>

          <div className="smart-filter-toggles">
            <label>
              <input
                type="checkbox"
                checked={filters.openNow}
                onChange={(event) => updateFilter("openNow", event.target.checked)}
              />
              Open now
            </label>
            <label>
              <input
                type="checkbox"
                checked={filters.familyFriendly}
                onChange={(event) =>
                  updateFilter("familyFriendly", event.target.checked)
                }
              />
              Family-friendly
            </label>
            <button type="button" onClick={resetFilters}>
              Reset filters
            </button>
          </div>
        </section>

        <div className="see-all-grid">
          {filteredItems.map((item, index) => {
            const fallback = getFallbackImages(item.name);
            const openStatus = getOpenStatus(item);
            const distanceKm = getDistanceFromKimberley(item);

            return (
              <Card
                key={index}
                title={item.name || "Untitled"}
                description={item.description || ""}
                image={fallback[0] || ""}
                gallery={fallback}
                rating={Number(item.rating) || 0}
                price={item.pricePerNight || item.price || 0}
                location={item.location || ""}
                category={item.category || ""}
                amenities={Array.isArray(item.amenities) ? item.amenities : []}
                phone={item.phone || ""}
                email={item.email || ""}
                mapLink={item.mapLink}
                ticketLink={item.ticketLink}
                cat={category}
                lineUp={Array.isArray(item.lineUp) ? item.lineUp : []}
                shops={Array.isArray(item.shops) ? item.shops : []}
                routes={Array.isArray(item.routes) ? item.routes : []}
                schedule={item.schedule || []}
                operator={item.operator || ""}
                openingHours={item.openingHours || ""}
                hours={item.hours || ""}
                openStatus={openStatus}
                distanceKm={distanceKm}
              />
            );
          })}
        </div>

        {!filteredItems.length && (
          <section className="smart-filter-empty">
            <h2>No matches found</h2>
            <p>Try widening your price, distance, rating, or open-now filters.</p>
            <button type="button" onClick={resetFilters}>
              Clear filters
            </button>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SeeAllPage;
